import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Add,
  BasicModal,
  Breadcrumbs,
  Button,
  CardComponent,
  DocComparison,
  DocParameter,
  DocReview,
  DocStepper,
  Group1,
  PageTitleAndSubTitle,
  TextField,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import ReportCreateNewLabelField from '../../_components/atoms/ReportCreateNewLabelField';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { commonDropDownProps } from '../../_components/atoms/ReportsCohortItem';
import { HOME_SCREEN, TERM_COMPILER } from '../../core-utils/routes';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import properties from '../../core-utils/properties';
import { Section } from '@HomaHealth/ui-component-library/lib/_AIQ-components/organisms/SortableTable';
import Snackbar from '../../_components/atoms/Snackbar';
import useSnackbar from '../../core-utils/custom-hooks/Snackbar';
import { DocumentsSelection } from '../../_components/organisms/DocumentsSelection';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import {
  compilationObjectProps,
  extractTermCompilerMetaData,
  fetchUseCases,
  updateCompiler,
} from '../../core-utils/helper-utils/termCompilerHelper';
import { RECOMMENDED_APPS } from '../../core-utils/messages/RecommendedAppConstants';
import { TermsSelection } from '../../_components/organisms/TermsSelection';
import OnBoardingPopUp from '../../_components/molecules/OnBoardingPopup';
import { getTextWithToolTip } from '../../core-utils/helper-utils/riskPatientDetailsHelper';
import { useStores } from '../../stores';

const useStyles = makeStyles()(() => ({
  breadCrumbsGrid: {
    margin: '10px',
  },
  hr: {
    width: '100%',
    border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    margin: '18px 0px',
  },
  mainDiv: {
    width: '1276px',
    padding: '20px 0px 36px 63px',
  },
  innerInputContainer: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  stepperGrid: {
    marginBottom: '40px',
  },
  buttonGrid: {
    marginTop: '50px',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '5px',
    paddingBottom: '10px',
  },
  compilationDetailsGrid: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
  },
}));

const NewTermCompilerPage: React.FC = observer(({}) => {
  //Constants
  const navigate = useNavigate();
  const { classes } = useStyles();
  const message = RECOMMENDED_APPS.TERM_COMPILER;
  const LoginPageStore = useStores().loginPageStore;
  const EmployerGroupStore = useStores().employerGroupStore;

  const breadcrumbItems = [
    [
      'text',
      'Home',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 500,
      },
      () => {
        navigate(HOME_SCREEN);
      },
    ],
    [
      'text',
      'Term Compiler',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 600,
      },
      () => {
        navigate(TERM_COMPILER);
      },
    ],
    [
      'text',
      'New Term Compiler',
      {
        color: patientAccessTheme.palette.text.highemphasis,
        fontWeight: 600,
      },
    ],
  ];
  const { openSnackbar, snackbarState, handleCloseSnackBar } = useSnackbar();

  // States
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [useCases, setUseCases] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const steps = [
    {
      icon: (
        <Group1 highlighted={currentTab === 0} completed={currentTab > 0} />
      ),
      title: 'Enter Basic Details',
      isActive: currentTab === 0,
      isCompleted: currentTab > 0,
    },
    {
      icon: (
        <DocComparison
          highlighted={currentTab === 1}
          completed={currentTab > 1}
        />
      ),
      title: 'Select Documents',
      isActive: currentTab === 1,
      isCompleted: currentTab > 1,
    },
    {
      icon: (
        <DocParameter
          highlighted={currentTab === 2}
          completed={currentTab > 2}
        />
      ),
      title: 'Select Topic & Terms',
      isActive: currentTab === 2,
      isCompleted: currentTab > 2,
    },
    {
      icon: <DocReview highlighted={currentTab === 3} completed={false} />,
      title: 'Data Ready For Review',
      isActive: currentTab === 3,
      isCompleted: false,
    },
  ];
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState<boolean>(
    true,
  );
  const [selectedContracts, setSelectedContracts] = useState<any[]>([]);
  const [selectedTerms, setSelectedTerms] = useState<Section[]>([]);
  const [
    compilationObject,
    setCompilationObject,
  ] = useState<compilationObjectProps>({
    term_compiler_id: '',
    group: '',
    group_name: '',
    compiler_name: '',
    description: '',
    use_case: '',
    use_case_name: '',
    is_default_use_case: false,
    contract_ids: [],
    plan_docs: [],
    selected_terms: [],
    status: '',
  });
  const [termSelectionComboPage, setTermSelectionComboPage] = useState<boolean>(
    false,
  );
  const [isAddUseCaseModalOpen, setIsAddUseCaseModalOpen] = useState<boolean>(
    false,
  );
  const [newUseCaseName, setNewUseCaseName] = useState<string>('');
  const [isDuplicateUseCase, setIsDuplicateUseCase] = useState(false);

  //Functions
  const handleNext = async (currentTab: number) => {
    switch (currentTab) {
      case 0:
        addNewCompiler();
        break;
      case 1:
        const contracts = selectedContracts
          .filter((temp: any) => !temp.isPlanDoc)
          .map((c: any) => c.id);
        const plan_docs = selectedContracts
          .filter((temp: any) => temp.isPlanDoc)
          .map((c: any) => c.id);
        setCompilationObject((prev) => ({
          ...prev,
          contract_ids: contracts,
          plan_docs,
        }));
        updateCompiler(
          { contracts, plan_docs },
          compilationObject.term_compiler_id,
          openSnackbar,
        );
        break;
      case 2:
        const formattedTerms = selectedTerms
          .map((section) => {
            if (section.rows.length > 0) {
              return {
                topic: section.topic,
                label: section.title,
                term_topics: section.rows.map((row: any, rowIndex: number) => ({
                  topic: row.name,
                  topic_description: row.value,
                  position: rowIndex + 1,
                   
                  terms: row.subrows.map((subrow: any, subIndex: number) => ({
                    term: subrow.name,
                    description: subrow.value,
                    key: subrow.key,
                    type: subrow.type,
                    position: subIndex + 1,
                  })),
                })),
              };
            }
            return null;
          })
          .filter(Boolean);
        updateCompiler(
          {
            status: 'Processing',
            selected_terms: formattedTerms,
          },
          compilationObject.term_compiler_id,
          openSnackbar,
        );
        const data = {
          term_compiler_id: compilationObject.term_compiler_id,
          eg_nid: compilationObject.group,
          selected_terms: formattedTerms,
          contracts: compilationObject.contract_ids,
          plan_docs: compilationObject.plan_docs,
        };
        extractTermCompilerMetaData(data, openSnackbar);
        break;
      default:
        break;
    }
  };

  const handleAddNewUseCase = async () => {
    setIsAddUseCaseModalOpen(false);
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.addUseCaseAndTopics,
        {
          eg_nid: compilationObject.group,
          organization: LoginPageStore.userResponse.organizationName,
          use_case: newUseCaseName,
          isDefault: false,
        },
      )
      .then((res: any) => {
        if (res.status === 200) {
          fetchUseCases(setUseCases, {
            eg_nid: compilationObject.group,
            organization: LoginPageStore.userResponse.organizationName,
          });
        }
      })
      .catch((err: any) => {
        openSnackbar(
          err?.error?.message ?? 'User Case Creation Failed',
          'error',
          3000,
        );
      });
  };

  const handleUseCaseChange = (e: any) => {
    const value = e.target.value;
    setNewUseCaseName(value);

    setIsDuplicateUseCase(
      useCases.some(
        (temp: any) =>
          temp.use_case?.toLowerCase().trim() === value.toLowerCase().trim(),
      ),
    );
  };

  //API calls
  const addNewCompiler = async () => {
    try {
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.contractBaseUrl +
            properties.addNewCompiler,
          {
            eg_nid: compilationObject.group,
            group_name: compilationObject.group_name,
            compiler_name: compilationObject.compiler_name,
            use_case: compilationObject.use_case,
            description: compilationObject.description,
          },
        )
        .then((res: any) => {
          if (res.status === 200) {
            setCompilationObject((prev) => ({
              ...prev,
              term_compiler_id: res?.data.data._id,
            }));
          }
        });
    } catch (error: any) {
      openSnackbar('Adding compilation Failed', 'error', 6000);
      throw error;
    }
  };

  //UseEffects
  useEffect(() => {
    const allGroups = EmployerGroupStore.allEmployerGroupsData;
    setGroups(allGroups);
  }, []);

  useEffect(() => {
    if (currentTab === 0) {
      setIsNextButtonDisabled(
        !(
          compilationObject?.compiler_name &&
          compilationObject?.use_case &&
          compilationObject?.group
        ),
      );
    } else if (currentTab === 1) {
      setIsNextButtonDisabled(selectedContracts.length === 0);
    } else if (currentTab === 2) {
      const disabled = selectedTerms.some((s: any) => s.topic?.includes('+'))
        ? termSelectionComboPage
          ? !selectedTerms.some((section) => section.rows.length > 0)
          : true
        : !selectedTerms.some((section) => section.rows.length > 0);
      setIsNextButtonDisabled(disabled);
    } else {
      setIsNextButtonDisabled(true);
    }
  }, [
    currentTab,
    compilationObject?.compiler_name,
    compilationObject?.use_case,
    compilationObject?.group,
    selectedContracts,
    selectedTerms,
    termSelectionComboPage,
  ]);

  //Components
  const CompilationDetails = () => {
    return (
      <CardComponent
        title={''}
        body={
          <Grid className={classes.compilationDetailsGrid}>
            <Grid style={{ width: '420px' }}>
              <Typography
                fontType="medium"
                fontSize="12px"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
              >
                Group Name
              </Typography>
              <Typography
                fontType="medium"
                fontSize="14px"
                textColor={patientAccessTheme.palette.text.highemphasis}
              >
                {compilationObject.group_name}
              </Typography>
            </Grid>
            <Grid style={{ width: '330px' }}>
              <Typography
                fontType="medium"
                fontSize="12px"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
              >
                Compilation Name
              </Typography>
              <Typography
                fontType="medium"
                fontSize="14px"
                textColor={patientAccessTheme.palette.text.highemphasis}
              >
                {compilationObject.compiler_name}
              </Typography>
            </Grid>
            <Grid style={{ width: '330px' }}>
              <Typography
                fontType="medium"
                fontSize="12px"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
              >
                Use Case
              </Typography>
              <Typography
                fontType="medium"
                fontSize="14px"
                textColor={patientAccessTheme.palette.text.highemphasis}
              >
                {compilationObject.use_case_name}
              </Typography>
            </Grid>
            {compilationObject.description && (
              <Grid style={{ width: '1120px' }}>
                <Typography
                  fontType="medium"
                  fontSize="12px"
                  textColor={patientAccessTheme.palette.text.mediumemphasis}
                >
                  Description
                </Typography>
                <Typography
                  fontType="medium"
                  fontSize="14px"
                  textColor={patientAccessTheme.palette.text.highemphasis}
                >
                  {compilationObject.description}
                </Typography>
              </Grid>
            )}
          </Grid>
        }
        mainGridStyles={{ marginBottom: '20px' }}
      />
    );
  };

  const FillCompilationDetails = () => {
    return (
      <CardComponent
        title={message.CARD_TITLE1}
        body={
          <Grid>
            <hr className={classes.hr} />
            <Grid className={classes.innerInputContainer}>
              <ReportCreateNewLabelField
                label={'Group Name'}
                component={
                  <Dropdown
                    label="Select Group"
                    dropDownOptions={groups?.map(
                      (item) => item?.employer_group_name,
                    )}
                    dropDownValues={groups?.map(
                      (item) => item?.employer_group_name,
                    )}
                    value={compilationObject.group_name}
                    handleChange={(event) => {
                      const selectgroup_name = event.target.value as any;
                      const eg_nid = groups.find(
                        (g: any) => selectgroup_name === g.employer_group_name,
                      ).eg_nid;
                      setCompilationObject((prev) => ({
                        ...prev,
                        group: eg_nid,
                        group_name: selectgroup_name,
                      }));
                      fetchUseCases(setUseCases, {
                        eg_nid,
                        organization:
                          LoginPageStore.userResponse.organizationName,
                      });
                    }}
                    style={{
                      width: '368px',
                    }}
                    selectStyles={{
                      ...commonDropDownProps.selectStyles,
                      width: '100%',
                    }}
                    labelTop="10px"
                  />
                }
              />
              <ReportCreateNewLabelField
                label="Compilation Name"
                width="368px"
                component={
                  <TextField
                    value={compilationObject.compiler_name}
                    placeholder="Enter Compiler Name"
                    height="36px"
                    width="100%"
                    fullWidth
                    onChange={(e: any) => {
                      setCompilationObject((prev) => ({
                        ...prev,
                        compiler_name: e.target.value,
                      }));
                    }}
                  />
                }
                marginBottom="12px"
              />
              <ReportCreateNewLabelField
                label={'Use Case'}
                component={
                  <>
                    <Dropdown
                      label="Select Use Case"
                      dropDownOptions={useCases.map((temp: any) =>
                        getTextWithToolTip(temp.use_case, 30),
                      )}
                      dropDownValues={useCases.map((temp: any) => temp._id)}
                      value={compilationObject.use_case}
                      handleChange={(event) => {
                        const selectedValue = event.target.value as any;
                        const use_case_name = useCases.find(
                          (temp: any) => temp._id === selectedValue,
                        );
                        setCompilationObject((prev) => ({
                          ...prev,
                          use_case: selectedValue,
                          use_case_name:
                            use_case_name && use_case_name?.use_case,
                          is_default_use_case: use_case_name?.default,
                        }));
                      }}
                      style={{
                        width: '368px',
                      }}
                      selectStyles={{
                        ...commonDropDownProps.selectStyles,
                        width: '100%',
                      }}
                      labelTop="10px"
                    />
                    {compilationObject.group === '' && (
                      <p
                        style={{
                          color: 'red',
                          fontSize: '12px',
                          marginTop: '4px',
                        }}
                      >
                        {'Group is required before selecting a use case'}
                      </p>
                    )}
                  </>
                }
                topRightItem={
                  <Typography
                    fontSize="11px"
                    lineHeight="22px"
                    textColor={patientAccessTheme.palette.primary[500]}
                    styleProps={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px',
                    }}
                    onClick={() =>
                      compilationObject.group_name &&
                      setIsAddUseCaseModalOpen(true)
                    }
                  >
                    {'Add Custom'}
                    <Add
                      stroke={patientAccessTheme.palette.primary[500]}
                      fill={patientAccessTheme.palette.primary[500]}
                    />
                  </Typography>
                }
              />
              <ReportCreateNewLabelField
                label="Description (Optional)"
                width="1120px"
                component={
                  <TextField
                    value={compilationObject.description}
                    placeholder="Enter Here"
                    height="66px"
                    width="100%"
                    fullWidth
                    multiline
                    maxRows={2}
                    onChange={(e: any) => {
                      setCompilationObject((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }));
                    }}
                  />
                }
                marginBottom="12px"
              />
            </Grid>
            <BasicModal
              title={'Use Case'}
              open={isAddUseCaseModalOpen}
              body={
                <ReportCreateNewLabelField
                  label="Add new use case"
                  width="100%"
                  component={
                    <>
                      <TextField
                        value={newUseCaseName}
                        fullWidth
                        height="36px"
                        onChange={handleUseCaseChange}
                        placeholder="Enter Name"
                        error={isDuplicateUseCase}
                      />
                      {isDuplicateUseCase && (
                        <p
                          style={{
                            color: 'red',
                            fontSize: '12px',
                            marginTop: '4px',
                          }}
                        >
                          {'This use case name already exists'}
                        </p>
                      )}
                    </>
                  }
                />
              }
              cancelButtonLabel={'Cancel'}
              confirmButtonLabel={'Add'}
              handleCancel={() => {
                setIsAddUseCaseModalOpen(false);
              }}
              handleConfirm={() => handleAddNewUseCase()}
              handleClose={() => setIsAddUseCaseModalOpen(false)}
            />
          </Grid>
        }
      />
    );
  };

  const SelectCompilationDocuments = () => {
    return (
      <>
        {CompilationDetails()}
        <CardComponent
          title={'Select Contract/ Plan Documents'}
          body={
            <Grid
              style={{
                borderTop: `solid 1px ${patientAccessTheme.palette.grey[300]}`,
                marginTop: '15px',
              }}
            >
              <DocumentsSelection
                setSelectedContracts={setSelectedContracts}
                eg_nid={compilationObject.group}
                hideUploadOption={true}
                hideFilter={true}
                showPlanDocs={true}
              />
            </Grid>
          }
        />
      </>
    );
  };

  const AddTerms = () => {
    return (
      <>
        {CompilationDetails()}
        <TermsSelection
          selectedTerms={selectedTerms}
          setSelectedTerms={setSelectedTerms}
          compilationObject={compilationObject}
          setCompilationObject={setCompilationObject}
          termSelectionComboPage={termSelectionComboPage}
          setTermSelectionComboPage={setTermSelectionComboPage}
        />
      </>
    );
  };

  const ProcessingDocuments = () => {
    return (
      <OnBoardingPopUp
        title="Documents are being processed"
        subTitle="This may take a few minutes. You can navigate to other sections and come back later."
        statusTitles={[]}
        locateTitle={'Click Here'}
        locateLink={TERM_COMPILER}
        infoLabel='You can return to the "Term Compiler" page to check the status.'
        rootStyles={{
          border: 'none',
          padding: '0px 10px',
        }}
      />
    );
  };

  const getStepsContent = () => {
    switch (currentTab) {
      case 0:
        return FillCompilationDetails();
      case 1:
        return SelectCompilationDocuments();
      case 2:
        return AddTerms();
      case 3:
        return ProcessingDocuments();
      default:
        return null;
    }
  };

  return (
    <>
      <Grid className={classes.breadCrumbsGrid}>
        <Breadcrumbs itemList={breadcrumbItems} />
      </Grid>
      <hr className={classes.hr} />
      <Grid className={classes.mainDiv}>
        <PageTitleAndSubTitle title={message.TITLE1} subTitle={''} />
        <CardComponent
          title={''}
          mainGridStyles={{
            marginTop: '10px',
          }}
          body={
            <>
              <Grid className={classes.stepperGrid}>
                <DocStepper statusTitles={steps} />
              </Grid>
              {getStepsContent()}
              {currentTab < 3 && (
                <Grid className={classes.buttonGrid}>
                  <Button
                    color="primary"
                    variant="contained"
                    height="43px"
                    style={{
                      padding: '8px 20px',
                      textTransform: 'none',
                    }}
                    label={currentTab === 2 ? 'Submit' : 'Next'}
                    disabled={isNextButtonDisabled}
                    onClick={() => {
                      handleNext(currentTab);
                      setCurrentTab((prev) => prev + 1);
                    }}
                  />
                </Grid>
              )}
            </>
          }
        />
      </Grid>
      <Snackbar
        {...snackbarState}
        vertical={'bottom'}
        horizontal={'right'}
        handleClose={handleCloseSnackBar}
      />
    </>
  );
});

export default NewTermCompilerPage;
